import React, { useRef, useEffect } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import { Style, Stroke, Fill, Text, Icon } from "ol/style";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Polygon from "ol/geom/Polygon";
import { fromLonLat } from "ol/proj";
import Overlay from "ol/Overlay";
import { defaults as defaultControls } from "ol/control";
import ImageLayer from "ol/layer/Image";
import ImageStatic from "ol/source/ImageStatic";
import Projection from "ol/proj/Projection";
import { defaults as defaultInteractions } from "ol/interaction";

declare const window: any;

// 🔹 Generador de fallback SVG como data:image
const createSvgIcon = (text: string, withLogo: boolean, colors: any) => {
  const fontSize = 12;
  const marginX = 4;
  const marginLeft = withLogo ? 25 : 10; // margen izquierdo extra para que sobresalga
  const charWidth = fontSize * 0.6;
  const textWidth = text.length * charWidth;
  const rectWidth = textWidth + 2 * marginX + marginLeft;

  // ⚠️ La esfera se centrará en el centro del SVG
  const sphereRadius = 25;
  const svgWidth = (rectWidth + sphereRadius) * 2;
  const svgHeight = sphereRadius * 2 + 2; // esfera ocupa todo el alto

  const centerX = svgWidth / 2;
  const centerY = svgHeight / 2;

  const rectX = centerX; // rectángulo empieza donde termina la esfera
  const rectY = centerY - 15; // centrado verticalmente
  const rectHeight = 30;

  const svgString = `
<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}">
  <!-- Rectángulo detrás -->
  <rect x="${rectX}" y="${rectY}" width="${rectWidth}" height="${rectHeight}" fill="${
    colors?.main_buttons
  }" rx="5" ry="5"/>
  <!-- Texto encima del rectángulo -->
  <text x="${rectX + marginLeft + textWidth / 2}" y="${centerY + fontSize / 3}"
        text-anchor="middle" font-size="${fontSize}" fill="white" font-family="Arial, sans-serif">${text}</text>
  <!-- Esfera centrada en el punto -->
  ${
    withLogo
      ? `<circle cx="${centerX}" cy="${centerY}" r="${sphereRadius}" fill="white" stroke="black" stroke-width="2"/>`
      : ""
  }
</svg>
    `;

  const base64 = btoa(unescape(encodeURIComponent(svgString)));
  return `data:image/svg+xml;base64,${base64}`;
};

// 🔹 Decide qué icon usar
const getPoiIcon = (poi: any, withLogo: any, colors: any): string => {
  if (
    poi.icon &&
    (poi.icon.startsWith("http") || poi.icon.startsWith("data:image"))
  ) {
    return poi.icon;
  }
  return createSvgIcon(poi.name || "", withLogo, colors);
};

const MapComponent = ({
  data,
  isSelectedVIA,
  isMapRenderVIAS,
  isMapRenderPOIS,
  isSelectedPOI,
  colors,
}) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<Map | null>(null);

  // --- MODO MAPA NORMAL ---
  useEffect(() => {
    if (isMapRenderVIAS || isMapRenderPOIS) return;
    if (!mapRef.current || !data) return;

    const features: Feature[] = [];

    // Polígono de delimitación
    if (data?.coordenates_delimitations?.length) {
      const polygonCoordinates = data.coordenates_delimitations.map((coord) =>
        fromLonLat([coord.lng, coord.lat])
      );
      const polygon = new Polygon([polygonCoordinates]);
      const polygonFeature = new Feature({ geometry: polygon });
      polygonFeature.setStyle(
        new Style({
          stroke: new Stroke({ color: data?.color_delimitation, width: 2 }),
          fill: new Fill({ color: hexToRGBA(data?.color_delimitation, 0.45) }),
        })
      );
      features.push(polygonFeature);
    }

    // POIs
    if (isSelectedPOI?.length > 0) {
      console.log(isSelectedPOI);
      
      isSelectedPOI.forEach((pois: any) => {
        console.log(pois);
        
        pois?.PoisArray.forEach((poi: any) => {
          const poiLat = parseFloat(poi.latitud ?? poi.center?.lat);
          const poiLng = parseFloat(poi.longitud ?? poi.center?.lng);
          const coord = fromLonLat([
            poiLng,
            pois.flag ? poiLat * 1.00018 : poiLat,
          ]);
          const coordIcon = fromLonLat([
            poiLng,
            pois.flag ? poiLat * 1.00018 : poiLat,
          ]);

          // 🟢 Icono principal
          const featureIcon = new Feature({
            geometry: new Point(coord),
            name: poi.name,
            poiId: poi.id,
          });

          featureIcon.setStyle(
            new Style({
              image: new Icon({
                src: getPoiIcon(poi, pois.withLogo, colors),
                scale: 1,
                anchorXUnits: "fraction",
                anchorYUnits: "fraction",
              }),
            })
          );
          features.push(featureIcon);

          // 🟠 Logo opcional
          if (pois.withLogo && pois.icon) {
            const logoUrl = pois.icon.startsWith("http")
              ? pois.icon
              : `https://d1bsymnertnea6.cloudfront.net/${pois.icon}`;

            const fontSize = 12;
            const marginX = 4;
            const marginLeft = 25; // margen izquierdo extra para que sobresalga
            const charWidth = fontSize * 0.6;
            const textWidth = poi.name.length * charWidth;
            const rectWidth = textWidth + 2 * marginX + marginLeft;

            // Calculamos el offset de la esfera
            const sphereRadius = 25; // mismo valor que en createSvgIcon
            const svgWidth = (rectWidth + sphereRadius) * 2;
            const centerX = svgWidth / 2;
            const offsetX = centerX / 2; // posición de la esfera en el SVG

            // Coordenadas del logo ajustadas
            const coordIconAdjusted = fromLonLat([
              poiLng - offsetX * 0.000042, // <-- aquí aplicas un factor de escala muy pequeño según tu zoom/escala
              pois.flag ? poiLat * 1.00018 : poiLat,
            ]);

            const featureLogo = new Feature({
              geometry: new Point(coord),
              name: poi.name,
              poiId: poi.id,
            });

            featureLogo.setStyle(
              new Style({
                image: new Icon({
                  src: logoUrl,
                  scale: 0.15,
                  anchor: [0.5, 0.5],
                  anchorXUnits: "fraction",
                  anchorYUnits: "fraction",
                }),
              })
            );
            features.push(featureLogo);
          }

          // 🔵 Líneas + Etiqueta
          if (pois.flag) {
            const nuevaLatitud = poiLat * 1.00018;
            const nuevaLongitud = poiLng;

            const lineFeature = new Feature({
              geometry: new LineString([
                fromLonLat([poiLng, poiLat]),
                fromLonLat([nuevaLongitud, nuevaLatitud]),
              ]),
            });
            lineFeature.setStyle(
              new Style({
                stroke: new Stroke({ color: "#000", width: 2 }),
              })
            );
            features.push(lineFeature);
          }
        });
      });
    }

    // Vías
    if (isSelectedVIA?.length > 0) {
      isSelectedVIA.forEach((via) => {
        const lineCoordinates = via.trace.map((point) =>
          fromLonLat([point.lng, point.lat])
        );
        const lineFeature = new Feature({
          geometry: new LineString(lineCoordinates),
          name: via.name,
        });
        lineFeature.setStyle(
          new Style({
            stroke: new Stroke({ color: via.color, width: 6 }),
          })
        );
        features.push(lineFeature);
      });
    }

    const vectorSource = new VectorSource({ features });
    const vectorLayer = new VectorLayer({ source: vectorSource });

    const map = new Map({
      target: mapRef.current,
      layers: [new TileLayer({ source: new OSM() }), vectorLayer],
      view: new View({
        center: fromLonLat([data.center.lng, data.center.lat]),
        zoom: data.defaultZoom,
      }),
      controls: defaultControls({ zoom: false }),
      interactions: !data?.interactive ? [] : defaultInteractions(),
    });

    mapInstance.current = map;

    // Logo proyecto
    if (data?.logo_proyecto) {
      const iconContainer = document.createElement("div");
      iconContainer.style.width = "70px";
      iconContainer.style.height = "70px";
      iconContainer.style.backgroundImage = `url(${
        window.hybrid ? "./" : "https://d1bsymnertnea6.cloudfront.net/"
      }${data.logo_proyecto.icon})`;
      iconContainer.style.backgroundSize = "contain";
      iconContainer.style.backgroundRepeat = "no-repeat";

      const iconOverlay = new Overlay({
        position: fromLonLat([
          data.logo_proyecto.center.lng,
          data.logo_proyecto.center.lat,
        ]),
        positioning: "center-center",
        element: iconContainer,
        stopEvent: false,
      });

      map.addOverlay(iconOverlay);
    }

    // Ajustar encuadre
    if (features.length > 0) {
      const extent = vectorSource.getExtent();

      if (isSelectedVIA?.length > 0 && data?.zoom_vias) {
        map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 800 });
        map.getView().setZoom(parseInt(data.zoom_vias));
      } else if (isSelectedPOI?.length === 1 && isSelectedPOI[0].zoom) {
        const poiGroup = isSelectedPOI[0];
        map
          .getView()
          .setCenter(
            fromLonLat([
              parseFloat(poiGroup.longitud),
              parseFloat(poiGroup.latitud),
            ])
          );
        map.getView().setZoom(parseInt(poiGroup.zoom));
      } /* else {
        map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 800 });
      } */
    }

    return () => {
      map.setTarget(undefined);
    };
  }, [data, isSelectedVIA, isSelectedPOI,  isMapRenderVIAS, isMapRenderPOIS]);

  // --- MODO MAPA ILUSTRADO ---
  // --- MODO MAPA ILUSTRADO ---
  useEffect(() => {
    if (!isMapRenderVIAS ) return;
    if (!mapRef.current || !data?.mapa_ilustrado_vias ) return;

    console.log(`${
      window.hybrid ? "" : "https://d1bsymnertnea6.cloudfront.net/"
    }${data.mapa_ilustrado_vias}`);
    

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = `${
      window.hybrid ? "" : "https://d1bsymnertnea6.cloudfront.net/"
    }${data.mapa_ilustrado_vias}`;

    console.log("🔎 Cargando ilustrado:", img.src);

    img.onload = () => {
      const width = img.width;
      const height = img.height;
      console.log("✅ Imagen ilustrada cargada:", width, "x", height);

      const extent: [number, number, number, number] = [0, 0, width, height];

      const projection = new Projection({
        code: "custom-image",
        units: "pixels",
        extent,
      });

      const imageLayer = new ImageLayer({
        source: new ImageStatic({
          url: img.src,
          projection,
          imageExtent: extent,
          crossOrigin: "anonymous",
        }),
      });

      const map = new Map({
        target: mapRef.current!,
        layers: [imageLayer],
        view: new View({
          projection,
          minZoom: 0,
          maxZoom: 8,
        }),
        controls: [],
      });

      // Ajustar la vista para que muestre toda la imagen
      map.getView().fit(extent, { size: map.getSize(), duration: 0 });

      mapInstance.current = map;
    };

    img.onerror = (err) => {
      console.error("❌ Error cargando ilustrado:", err, img.src);
    };

    return () => {
      if (mapInstance.current) mapInstance.current.setTarget(undefined);
    };
  }, [isMapRenderVIAS, data]);
  useEffect(() => {
    if (!isMapRenderPOIS) return;
    if (!mapRef.current || !data?.mapa_ilustrado_pois) return;

    console.log(`${
      window.hybrid ? "" : "https://d1bsymnertnea6.cloudfront.net/"
    }${data.mapa_ilustrado_pois}`);
    

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = `${
      window.hybrid ? "" : "https://d1bsymnertnea6.cloudfront.net/"
    }${ data.mapa_ilustrado_pois}`;

    console.log("🔎 Cargando ilustrado:", img.src);

    img.onload = () => {
      const width = img.width;
      const height = img.height;
      console.log("✅ Imagen ilustrada cargada:", width, "x", height);

      const extent: [number, number, number, number] = [0, 0, width, height];

      const projection = new Projection({
        code: "custom-image",
        units: "pixels",
        extent,
      });

      const imageLayer = new ImageLayer({
        source: new ImageStatic({
          url: img.src,
          projection,
          imageExtent: extent,
          crossOrigin: "anonymous",
        }),
      });

      const map = new Map({
        target: mapRef.current!,
        layers: [imageLayer],
        view: new View({
          projection,
          minZoom: 0,
          maxZoom: 8,
        }),
        controls: [],
      });

      // Ajustar la vista para que muestre toda la imagen
      map.getView().fit(extent, { size: map.getSize(), duration: 0 });

      mapInstance.current = map;
    };

    img.onerror = (err) => {
      console.error("❌ Error cargando ilustrado:", err, img.src);
    };

    return () => {
      if (mapInstance.current) mapInstance.current.setTarget(undefined);
    };
  }, [isMapRenderPOIS, data]);


  const hexToRGBA = (hex: string, alpha = 1) => {
    hex = hex.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div
      className="map-container"
      ref={mapRef}
      style={{ position: "relative", height: "100%", width: "100%" }}
    />
  );
};

export default MapComponent;
